/**
 * Speaker profiles -- group 02 (British monitor lineage).
 *
 * Several of these share the same BBC ancestry, so the voicing offsets are
 * where the real differentiation lives: a 5-inch sealed miniature, an 8-inch
 * reflex monitor and a 12-inch monitor crossed at a few hundred hertz do not
 * behave alike even when all three were designed to the same house curve. The
 * remaining entries deliberately break the pattern -- a damped transmission
 * line, a sealed tri-amplified active, a modern ported three-way, a ribbon
 * standmount and an upward-firing miniature floorstander.
 */

import type { SpeakerProfile } from '../../types/hardware';
import { impedanceCurve, makeSpeaker, usd } from '../kit';

export const SPEAKERS_02: SpeakerProfile[] = [
  makeSpeaker({
    mfr: 'PMC',
    model: 'twenty5.24i',
    price: usd(7500, 'APPROXIMATE', 2021, 'Approximately USD 7,500 per pair. APPROXIMATE.'),
    year: 2021,
    country: 'United Kingdom',
    speakerType: 'floorstander',
    enclosure: 'transmission-line',
    drivers: ['dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 86,
    nominalOhms: 8,
    minOhms: 5,
    bass: [27, 23],
    hfExtension: 25000,
    crossovers: [[1700, 24]],
    responseTilt: [[20, 0], [45, 0.6], [110, -0.3], [400, 0], [1200, 0.3], [3000, -0.5], [7000, 0.4], [12000, 0.2], [20000, -1.0]],
    resonances: [[120, -34, 9], [430, -40, 13]],
    coverageAngleDeg: 110,
    recommendedWatts: [30, 200],
    dimensionsMm: [1043, 188, 419],
    massKg: 24,
    thdPercent: 0.3,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#4a7fb5',
    tags: ['transmission-line', 'atl', 'studio-lineage'],
    notes:
      'The rear of the bass driver feeds a long folded and heavily damped line whose quarter-wave output reinforces the low frequencies an octave below where a reflex port in the same box volume could. Because the line is damped rather than tuned, the profile carries a second-order low-frequency rolloff with a long low-level tail instead of the fourth-order slope and sharp impedance saddle a ported alignment produces, and there is no port organ-pipe resonance to model. Crossing at 1.7 kHz keeps the 170 mm bass unit inside its piston band, so the power response through the crossover does not narrow as abruptly as a two-way crossed higher.',
    provenanceNote:
      'Transmission-line loading, crossover point, sensitivity and bass extension follow PMC published material; dimensions and mass are approximate. The impedance curve is MODELLED with a single low-frequency resonance, which is a simplification -- a real damped line shows two smaller peaks rather than one -- and this simplification is recorded here rather than hidden.',
  }),

  makeSpeaker({
    mfr: 'ATC',
    model: 'SCM50ASL',
    price: usd(18000, 'APPROXIMATE', 2020, 'Approximately USD 18,000 per pair. APPROXIMATE.'),
    year: 1995,
    country: 'United Kingdom',
    speakerType: 'active',
    enclosure: 'sealed',
    drivers: ['dynamic'],
    driverCount: 3,
    wayCount: 3,
    active: true,
    sensitivityDb: 94,
    nominalOhms: 8,
    minOhms: 7.6,
    bass: [38, 33],
    hfExtension: 22000,
    crossovers: [[380, 24], [3500, 24]],
    impedance: impedanceCurve(
      [[10, 8, 0], [100, 8, 0], [1000, 8, 0], [10000, 8, 0], [20000, 8, 0]],
      8,
      'MODELLED',
    ),
    responseTilt: [[20, 0], [50, 0.2], [150, -0.4], [500, 0.1], [1500, 0], [3000, -0.6], [6000, 0.2], [12000, -0.4], [20000, -1.2]],
    resonances: [[210, -42, 16], [520, -46, 20]],
    coverageAngleDeg: 100,
    dimensionsMm: [890, 430, 480],
    massKg: 62,
    thdPercent: 0.15,
    roomCoupling: 'low',
    confidence: 'ESTIMATED',
    accent: '#5c6470',
    tags: ['active', 'tri-amplified', 'soft-dome-midrange', 'sealed', 'studio-monitor'],
    notes:
      'The defining component is a 75 mm soft-dome midrange with a very large motor covering 380 Hz to 3.5 kHz, which puts every crossover discontinuity outside the band where the ear is most sensitive to them and gives an almost constant directivity through the vocal range. Because each driver has its own amplifier behind an active crossover, there is no passive network impedance between amplifier and voice coil, so the modelled power compression is lower and the crossover slopes are exactly the electrical ones rather than an acoustic approximation. The sealed alignment gives a second-order rolloff, so it loses output gradually below 38 Hz and integrates with room gain rather than fighting it.',
    provenanceNote:
      'Driver complement, sealed loading, tri-amplification and the 380 Hz / 3.5 kHz crossover points follow ATC published material. Two figures are deliberately artificial: this speaker takes a line-level balanced input rather than an amplifier load, so the flat 8 ohm impedance recorded here is a placeholder that makes the amplifier-interaction model a no-op, and the sensitivity value represents equivalent system gain rather than a published dB / 2.83 V figure. Year is the approximate introduction of the SL series, which has been revised repeatedly since.',
  }),

  makeSpeaker({
    mfr: 'Spendor',
    model: 'D7.2',
    price: usd(6995, 'APPROXIMATE', 2019, 'Approximately USD 6,995 per pair. APPROXIMATE.'),
    year: 2019,
    country: 'United Kingdom',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 3,
    wayCount: 3,
    sensitivityDb: 89,
    nominalOhms: 8,
    minOhms: 5.5,
    bass: [29, 25],
    hfExtension: 25000,
    portTuningHz: 32,
    crossovers: [[900, 18], [4200, 18]],
    responseTilt: [[20, 0], [60, 0.4], [160, -0.5], [400, -0.3], [1000, 0.2], [2500, 0.5], [5000, 0.6], [10000, 0.4], [20000, -0.8]],
    resonances: [[220, -38, 12], [560, -42, 16]],
    coverageAngleDeg: 115,
    recommendedWatts: [25, 200],
    dimensionsMm: [950, 192, 315],
    massKg: 20.5,
    thdPercent: 0.28,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#a88f6a',
    tags: ['lpz-tweeter', 'braced-cabinet', 'ported', 'modern-spendor'],
    notes:
      'This is the modern side of the house rather than the BBC one: the cabinet is braced and damped to push its resonances up in frequency and down in level, which is the opposite of the thin-wall approach used by the Rogers and Graham entries in this group. The tweeter sits behind a phase-correcting front plate that raises the frequency of its first breakup mode, so the top octave is extended without a sharp resonant peak, and the voicing carries a modest presence-region lift rather than the classic dip. A reflex alignment tuned near 32 Hz gives a fourth-order rolloff, so it extends lower than the sealed designs here but loses output faster once it goes.',
    provenanceNote:
      'Bass extension, sensitivity, driver complement and reflex loading follow Spendor published material; dimensions and mass are approximate. Crossover frequencies and port tuning are ESTIMATED from the driver complement because Spendor does not publish them, and the cabinet-resonance parameters are MODELLED from the stated braced construction.',
  }),

  makeSpeaker({
    mfr: 'Graham Audio',
    model: 'LS5/8',
    price: usd(14000, 'APPROXIMATE', 2020, 'Approximately USD 14,000 per pair. APPROXIMATE.'),
    year: 2016,
    country: 'United Kingdom',
    speakerType: 'standmount',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 89,
    nominalOhms: 8,
    minOhms: 6,
    bass: [40, 33],
    hfExtension: 16000,
    portTuningHz: 38,
    crossovers: [[650, 12]],
    responseTilt: [[20, 0], [50, 0.5], [120, 0.6], [300, 0.2], [900, -0.4], [2000, -1.0], [4000, 0.4], [8000, -0.6], [16000, -2.4]],
    resonances: [[150, -28, 6], [340, -32, 8]],
    coverageAngleDeg: 120,
    recommendedWatts: [50, 300],
    dimensionsMm: [780, 460, 340],
    massKg: 40,
    thdPercent: 0.35,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#8a7d5f',
    tags: ['bbc-lineage', 'large-monitor', 'thin-wall', 'low-crossover'],
    notes:
      'A 300 mm bass unit handed over to a 34 mm dome at a few hundred hertz is the defining choice: the big cone never has to work up into the band where its own diameter would beam, and the dome covers the entire presence region on its own, so horizontal directivity is wide and remarkably even through the crossover. The price of crossing a dome that low is limited ultimate extension, so the top octave is modelled as rolling away above 16 kHz rather than reaching 20. The thin-wall damped cabinet contributes low-Q resonances in the lower midrange rather than the sparse high-Q signature of a heavily braced box.',
    provenanceNote:
      'BBC lineage, the two-way large-monitor configuration and the thin-wall cabinet philosophy follow Graham Audio published material. Sensitivity, crossover frequency, port tuning, bass extension, dimensions and mass are all ESTIMATED for this profile and should not be read as quoted specifications; the crossover figure in particular is inferred from the driver complement.',
  }),

  makeSpeaker({
    mfr: 'Rogers',
    model: 'LS5/9 Classic',
    price: usd(6500, 'APPROXIMATE', 2021, 'Approximately USD 6,500 per pair. APPROXIMATE.'),
    year: 2019,
    country: 'United Kingdom',
    speakerType: 'standmount',
    enclosure: 'ported',
    drivers: ['dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 87,
    nominalOhms: 8,
    minOhms: 6.2,
    bass: [50, 42],
    hfExtension: 20000,
    portTuningHz: 48,
    crossovers: [[3000, 18]],
    responseTilt: [[20, 0], [60, -0.4], [150, 0.5], [400, 0.3], [1000, -0.3], [2000, -1.2], [3500, -0.6], [7000, 0.5], [12000, 0], [20000, -1.8]],
    resonances: [[190, -26, 6], [420, -30, 8]],
    coverageAngleDeg: 125,
    recommendedWatts: [25, 150],
    dimensionsMm: [460, 275, 280],
    massKg: 15,
    thdPercent: 0.4,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#9c6b4f',
    tags: ['bbc-lineage', 'bextrene-cone', 'thin-wall', 'presence-dip'],
    notes:
      'A 200 mm plastic-cone bass unit running all the way to 3 kHz is being asked to radiate above the frequency where its own diameter starts to beam, so on-axis output stays flat while off-axis output falls -- the mild broad dip in the voicing around 2 kHz is the compensation the BBC house curve applies for exactly that. Cone material is chosen for internal damping rather than stiffness, so breakup is smeared into a low-level rise rather than a sharp peak, at the cost of efficiency. Reflex tuning near 48 Hz sets a fourth-order rolloff, which keeps this comfortably shy of the true low bass but keeps midbass excursion in hand.',
    provenanceNote:
      'BBC lineage, two-way configuration, reflex loading, sensitivity and nominal impedance follow Rogers published material for the reissue. Bass extension, port tuning, crossover slope, dimensions and mass are ESTIMATED. Note that the brand is now operated by Rogers International outside the United Kingdom; the country recorded here is the design lineage, not necessarily the assembly location.',
  }),

  makeSpeaker({
    mfr: 'Falcon Acoustics',
    model: 'LS3/5a Gold Badge',
    price: usd(4000, 'APPROXIMATE', 2021, 'Approximately USD 4,000 per pair. APPROXIMATE.'),
    year: 2019,
    country: 'United Kingdom',
    speakerType: 'standmount',
    enclosure: 'sealed',
    drivers: ['dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 83,
    nominalOhms: 15,
    minOhms: 11,
    bass: [70, 58],
    hfExtension: 20000,
    crossovers: [[3000, 18]],
    responseTilt: [[20, 0], [80, 1.2], [150, 1.0], [400, -0.5], [900, 0.4], [1800, 1.0], [3000, -1.0], [6000, 0.6], [10000, 1.4], [20000, 0.8]],
    resonances: [[500, -24, 5], [900, -28, 7]],
    coverageAngleDeg: 135,
    recommendedWatts: [15, 60],
    dimensionsMm: [305, 190, 165],
    massKg: 5.4,
    thdPercent: 0.6,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#c2a878',
    tags: ['bbc-lineage', 'ls3-5a', 'sealed-miniature', 'high-impedance', 'complex-crossover'],
    notes:
      'A sealed 5-inch two-way with an elaborate 15 ohm crossover that spends most of its component count correcting the bass unit response and setting the intended balance, which is why sensitivity lands near 83 dB -- the design trades efficiency for a specific frequency response and it does so deliberately. The upper bass carries a broad lift because the narrow baffle loses several decibels below its step frequency and the voicing puts some of that back, which is also why the profile is marked high room coupling: it needs boundary reinforcement to sound as intended. Excursion limits arrive early on a small sealed driver, so modelled distortion rises steeply below about 95 Hz and the useful output ceiling is the lowest in this group.',
    provenanceNote:
      'The LS3/5a specification is a licensed BBC design, and the 15 ohm impedance, sealed loading, cabinet size and low sensitivity follow that specification and Falcon published material. Bass extension, crossover slope, distortion and cabinet resonance behaviour are ESTIMATED or MODELLED, not quoted. The voicing offsets reproduce the well-documented shape of the design rather than any individual measurement.',
  }),

  makeSpeaker({
    mfr: 'ProAc',
    model: 'Response D2R',
    price: usd(6500, 'APPROXIMATE', 2020, 'Approximately USD 6,500 per pair. APPROXIMATE.'),
    year: 2016,
    country: 'United Kingdom',
    speakerType: 'standmount',
    enclosure: 'ported',
    drivers: ['dynamic', 'ribbon'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 88.5,
    nominalOhms: 8,
    minOhms: 5.8,
    bass: [40, 33],
    hfExtension: 33000,
    portTuningHz: 42,
    crossovers: [[3800, 18]],
    responseTilt: [[20, 0], [50, 0.3], [120, -0.4], [500, -0.2], [1500, 0.3], [3000, 0.6], [6000, 0.9], [12000, 1.2], [20000, 1.4], [30000, 0.6]],
    resonances: [[260, -34, 10], [610, -38, 13]],
    coverageAngleDeg: 100,
    recommendedWatts: [20, 150],
    dimensionsMm: [420, 200, 265],
    massKg: 12,
    thdPercent: 0.25,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#6fa88a',
    tags: ['ribbon-tweeter', 'ported', 'rising-treble', 'high-hf-extension'],
    notes:
      'The ribbon is the reason this profile looks nothing like the dome-tweeter entries above it: a foil diaphragm of very low mass has no dome breakup mode inside or near the audio band, so its stored energy and its distortion above 10 kHz are far lower than a 25 mm dome and its usable extension runs past 30 kHz. The trade is directivity -- the radiating strip is tall relative to the wavelengths it handles, so vertical dispersion narrows sharply and listening height matters more than with any other speaker in this group. Voicing carries a genuine rising top end of roughly a decibel by 12 kHz rather than a flat or falling treble.',
    provenanceNote:
      'Ribbon tweeter, reflex loading and sensitivity follow ProAc published material. ProAc quotes a low-frequency limit of about 30 Hz at an unstated tolerance; the 40 Hz figure used here as -3 dB is a deliberately more conservative estimate for a 165 mm two-way and that substitution is recorded rather than hidden. Crossover frequency, port tuning and mass are ESTIMATED.',
  }),

  makeSpeaker({
    mfr: 'Neat Acoustics',
    model: 'Iota Alpha',
    price: usd(2995, 'APPROXIMATE', 2019, 'Approximately USD 2,995 per pair. APPROXIMATE.'),
    year: 2014,
    country: 'United Kingdom',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['dynamic', 'planar-panel'],
    driverCount: 3,
    wayCount: 3,
    sensitivityDb: 86,
    nominalOhms: 8,
    minOhms: 6,
    bass: [33, 28],
    hfExtension: 22000,
    portTuningHz: 36,
    crossovers: [[450, 12], [3500, 12]],
    responseTilt: [[20, 0], [70, -0.6], [120, 0.8], [300, 0.4], [800, -0.3], [2000, 0.5], [5000, 0.8], [10000, 0.3], [20000, -1.2]],
    resonances: [[280, -30, 8], [700, -34, 11]],
    coverageAngleDeg: 95,
    recommendedWatts: [25, 100],
    dimensionsMm: [450, 200, 165],
    massKg: 8.5,
    thdPercent: 0.45,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#b06f8f',
    tags: ['upward-firing-bass', 'planar-tweeter', 'compact-floorstander'],
    notes:
      'The bass driver fires upward out of the top of a 450 mm cabinet while the midrange and planar tweeter fire forward, which means the low-frequency output reaches the listener as ceiling-reflected and room-integrated energy rather than as direct sound on the listening axis. That is why this profile is marked high room coupling and why its quoted extension is far below what the enclosure volume alone would predict: the room is a load-bearing part of the design, not an error term. The planar tweeter has a small, light diaphragm driven across its whole area, so it contributes less stored energy than a dome but rolls off earlier than the ribbon entry here.',
    provenanceNote:
      'The upward-firing bass driver, planar tweeter and cabinet size follow Neat Acoustics published material. Sensitivity, bass extension, port tuning, crossover frequencies, mass and distortion are ESTIMATED, not quoted. The high room-coupling classification is a modelling judgement that follows directly from the upward-firing arrangement rather than from any manufacturer statement.',
  }),
];
